import { Combobox } from '@/components/Combobox';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/Command';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { AspNetUsers, AuditLog } from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { AuditLogAction, AuditLogActionMap } from '@/models/audit-log.models';
import { FeatureName } from '@/models/role.models';
import hasPermission from '@/utils/permission.function';
import { hasNonEmptyField } from '@/utils/search-param.function';
import { useQuery } from '@tanstack/react-query';
import { Table } from '@tanstack/react-table';
import { Check, ChevronsUpDown, Columns2, X } from 'lucide-react';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { allUserQuery } from '../graphql/query';

export type AuditHistoryFilter = {
  action?: string[];
  actor?: string | null;
};

type ListAuditHistoryHeaderProps = {
  table: Table<AuditLog>;
  filters: AuditHistoryFilter | undefined;
  setFilters: Dispatch<SetStateAction<AuditHistoryFilter | undefined>>;
};

export default function ListAuditHistoryHeader({
  table,
  filters,
  setFilters,
}: ListAuditHistoryHeaderProps) {
  const [userSelectionOpen, setUserSelectionOpen] = useState(false);
  const actions = useRef<string[]>(Object.values(AuditLogAction));
  const [users, setUsers] = useState<AspNetUsers[]>([]);
  const showResetFiltersButton = hasNonEmptyField(filters);

  const { data: allUserData } = useQuery({
    queryKey: ['allUser'],
    queryFn: () =>
      execute({
        query: allUserQuery,
      }),
    select: (data) => data,
    enabled: hasPermission(FeatureName.VIEW_LIST_USERS_PAGE),
  });

  useEffect(() => {
    if (allUserData) {
      setUsers(allUserData.aspNetUsers as AspNetUsers[]);
    }
  }, [allUserData]);

  const handleActorFilterValueChange = (username: string) => {
    const sub = users.find((u) => u.userName === username)?.sub;
    const selectedSub = sub === filters?.actor ? undefined : sub;
    setFilters({ ...filters, actor: selectedSub });
  };

  const handleClickResetFilters = () => {
    setFilters({});
  };

  const handleActionSelectionChange = (value: string[]) => {
    setFilters({
      ...filters,
      action: value.length > 0 ? value : [],
    });
  };

  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      {hasPermission(FeatureName.VIEW_LIST_USERS_PAGE) && (
        <div className="sm:w-[300px]">
          <Popover
            open={userSelectionOpen}
            onOpenChange={setUserSelectionOpen}
            modal={true}
          >
            <PopoverTrigger asChild className="w-full">
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={userSelectionOpen}
                className="w-full max-w-full justify-between"
              >
                {filters?.actor ? (
                  <span className="truncate">
                    {users.find((u) => u.sub === filters?.actor)?.userName}
                  </span>
                ) : (
                  <span className="text-muted-foreground truncate">
                    Filter by user...
                  </span>
                )}
                <ChevronsUpDown className="opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-1" align="end" side="bottom">
              <Command defaultValue={filters?.actor ?? ''}>
                <CommandInput placeholder={'Search'} className="h-9" />
                <CommandList>
                  <CommandEmpty className="text-muted-foreground flex justify-center py-2">
                    Not found.
                  </CommandEmpty>
                  <CommandGroup>
                    {users.map((u, idx) => {
                      return (
                        <CommandItem
                          key={idx}
                          value={u.userName as string}
                          onSelect={handleActorFilterValueChange}
                          className="flex justify-between"
                        >
                          {u.userName}
                          <div className="flex justify-between gap-1">
                            <span className="text-muted-foreground text-xs">
                              {u.aspNetUserRoles.map((role, index) => {
                                return (
                                  <Badge
                                    key={index}
                                    variant={'secondary'}
                                    className="text-xs capitalize"
                                  >
                                    {role.aspNetRole.name}
                                  </Badge>
                                );
                              })}
                            </span>
                            <div className="w-4">
                              {filters?.actor === u.sub && <Check />}
                            </div>
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}
      <div className="md:min-w-2xs">
        <Combobox
          selectedValues={filters?.action ?? []}
          onChange={handleActionSelectionChange}
          placeholder="Filter by actions"
          options={actions.current.sort().map((val) => {
            return {
              label: AuditLogActionMap[val as AuditLogAction],
              value: val,
            };
          })}
        />
      </div>
      <div className="flex gap-2 sm:ms-auto">
        {showResetFiltersButton && (
          <Button variant={'outline'} onClick={handleClickResetFilters}>
            <X />
            Reset
          </Button>
        )}
        <div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                <Columns2 />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                ?.getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
